<script lang="ts">
  import CtaButton from '../Generic/CtaButton.svelte'
  import { goto } from '@roxi/routify'
</script>

<div id="page-container">
  <div id="content-container">
    <section>
      <h1>TSF Retro App</h1>
    </section>

    <section id="join">
      <CtaButton on:click={() => void $goto('/sign_in')}>Join a Retro</CtaButton>
      <p>Sign in to an existing team to run regular retros and review results.</p>
      <p>To join a retro for the first time, follow the invite link shared by your team admin.</p>
    </section>

    <section id="sign-up">
      <CtaButton on:click={() => void $goto('/new_sign_up')}>Create a Retro</CtaButton>
      <p>Setup a new retro to start running retrospectives for your own team.</p>
    </section>

    <section id="definition">
      <h2>What is a retro?</h2>
      <p>
        A retro (or retrospective) is an activity where you talk about how things have gone with the goal of
        identifying potential improvements that you can start to apply.
      </p>
      <p>There is not one single approach to retrospectives, but common themes include:</p>
      <section>
        <p>
          <b>Full Team Participation</b> allows you to uncover different perspectives and priorities. Communication
          and interfaces between people are a common cause of friction which is best resolved when all parties
          are involved.
        </p>
      </section>
      <section>
        <p>
          To get the best reults your retrospective needs to be <b>A Safe Place</b> where all participants feel
          they can voice their opinions and that they will be listened to and respected.
        </p>
      </section>
      <section>
        <p>
          Run <b>Frequent Regular</b> retrospectives so that issues are raised while they are still fresh and relevant,
          and so you can enact changes early and reap the most benefit.
        </p>
      </section>
    </section>

    <section id="benefit">
      <h2>TSF Retro App</h2>
      <p>
        The TSF Retro App is a simple, friendly, cloud-hosted service that allows you to securely run
        retrospectives with your team, be they remote or office based.
      </p>
      <p>
        At <a href="https://tsf.tech">tsf.tech</a> we used to run our monthly retrospective in the office using
        paper cards. When the pandemic hit, we jumped to zoom but still used the paper cards. We built the first
        version of the TSF Retro App simply as a case study to trial new technologies in one of our Friday R&D
        sessions. This first version worked well for us and we immediately switched to using it for all our retrospectives.
        After a while we decided other teams would also benefit, so we added the extra features needed to bring
        the TSF Retro App to you.
      </p>
    </section>

    <section id="features">
      <h2>Features</h2>
      <section id="structure">
        <h3>Structured Topic Cards</h3>
        <img src="/images/card.png" alt="Card showing title and 3 statements" />
        <p>
          A deck of cards provides a set of topics that help you think about different aspects of your team's
          performance. Each card has three provocative statements to help participants frame a response. The
          topics and statements are far more effective than simply opening the floor for comments, which tends
          to be slow and daunting for many teams.
        </p>
      </section>
      <section id="remote">
        <h3>Remote Collaboration</h3>
        <p>
          Allow your team to join from anywhere, whether that be a shared meeting room or individual home
          office. All participants devices are synchronized, so you all see the same card and votes at the
          same time without needing to share a screen over zoom.
        </p>
        <img src="/images/synchronised.png" alt="Multiple synchronised views" />
      </section>
      <section id="shareable">
        <h3>Open and Shareable</h3>
        <p>
          Send an invite link to anyone with internet access, allowing you to run retrospectives within one
          organisation, with a multi-org project team, or even among a private group of friends.
        </p>
      </section>
      <section id="personal">
        <h3>Independent Voting</h3>
        <img src="/images/voted.png" alt="Vote cast indicators" />
        <p>
          Votes are hidden during voting to encourage participants to share their own opinions rather than
          following the herd.
        </p>
      </section>
      <section id="discuss">
        <h3>Reveal Votes</h3>
        <p>
          By sharing the votes for each card you encourage conversation and build a better understanding of
          your teammates' perspectives. Participants can continue to revise their votes if they are reminded
          about something they forgot or didn't notice.
        </p>
        <img src="/images/revealed.png" alt="Attendee votes revealed" />
      </section>
      <section id="control">
        <h3>Full Control</h3>
        <img src="/images/controls.png" alt="Control buttons" />
        <p>
          The Leader has full control. You can decide who to admit, how long to discuss a card, how many votes
          to wait for, and if and when to reveal individual votes during the retro.
        </p>
      </section>
      <section id="lobby">
        <h3>Lobby</h3>
        <p>
          Schedule or run ad hoc retrospectives whenever you like. The lobby is always open. See each
          participant arrive in the Lobby and have the option to evict any unexpected attendees.
        </p>
        <img src="/images/lobby.png" alt="Lobby showing attendees and join link" />
      </section>
      <section id="leader">
        <h3>Leader</h3>
        <img src="/images/attendees.png" alt="Leader selection and attendee eviction" />
        <p>
          The first participant to join is the default Leader, who can then pass control to another
          participant in the Lobby. The Leader is given the controls to Reveal Votes, move to Next Card, etc.
          During our own retrospectives, we rotate the Leader role giving everyone the chance to run the
          retrospective in their own way.
        </p>
      </section>
      <section id="history">
        <h3>History</h3>
        <p>
          After voting on all cards, see a summary of the results and compared to previous retrospectives.
          Focus your attentions on best or worst topics, most improved or degraded, or a topic with a downward
          trend or volatile history.
        </p>
        <img src="/images/history.png" alt="Past 3 results showing trend" />
      </section>
      <section id="ownership">
        <h3>Card Ownership</h3>
        <p>
          We provide an initial set of cards covering a range of topics suitable for most teams, but if you
          find a topic is not relevant or the descriptive text could be better targeted for your team, you
          have complete control to add, remove or edit any cards.
        </p>
      </section>
    </section>

    <section id="pricing">
      <h2>Pricing</h2>
      <section id="standard">
        <h3>Standard</h3>
        <p>Unlimited retrospectives. One team. One deck of cards. One history. Ten team members.</p>
        <p>1 month free, £5 per month</p>
      </section>
      <section id="bespoke">
        <h3>Bespoke</h3>
        <p>Anything else?</p>
        <p>Please contact us at <a href="mailto:retro@tsf.tech">retro@tsf.tech</a></p>
      </section>
    </section>

    <section id="credit">
      <h2>Credit</h2>
      <p>
        The TSF Retro App is based on the <a
          href="https://engineering.atspotify.com/2014/09/squad-health-check-model/"
          >Squad Health Check Model</a
        > developed at Spotify.
      </p>
    </section>
  </div>
</div>

<style>
  #page-container {
    width: 100%;
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 2em;
  }

  #page-container::before {
    content: '';
    width: 100%;
    height: 100vh;
    background-image: url('/images/Lesson.svg');
    background-size: cover;
    background-attachment: fixed;
    background-position: 50% 50%;
    position: fixed;
    top: 0em;
    left: 0em;
    z-index: -1;
  }

  #content-container {
    height: 100%;
    max-width: 60em;
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    justify-content: start;
    box-sizing: border-box;
  }

  section {
    width: 100%;
    background-color: rgba(0, 100, 255, 0.1);
    border-radius: 1em;
    margin: 1em 0em;
    padding: 1em;
    box-sizing: border-box;
  }

  h1 {
    color: var(--tsf-blue);
    font-size: 5em;
  }
  h2 {
    color: var(--tsf-black);
    font-size: 2em;
    text-align: left;
  }
  h3 {
    color: var(--tsf-black);
    font-size: 1.5em;
    text-align: left;
  }
  p {
    color: var(--tsf-black);
    font-size: 1.25em;
    text-align: justify;
  }
  b {
    font-size: 1.1em;
    text-transform: capitalize;
    text-decoration: underline;
  }

  #join p,
  #sign-up p {
    text-align: center;
  }

  #join,
  #sign-up {
    padding-top: 1em;
    text-align: center;
  }

  #definition section p {
    text-align: center;
  }

  #features section {
    display: flex;
    flex-flow: row wrap;
    column-gap: 1em;
    justify-content: center;
    align-items: center;
  }
  #features section h3 {
    flex: 0 0 100%;
  }
  #features section p {
    flex: 1 0 50%;
  }
  #features section img {
    object-fit: contain;
    border-radius: 1em;
    border: 0.2em solid var(--tsf-washed-grey);
    max-height: 25vh;
    max-width: 33%;
  }

  @media screen and (max-width: 600px) {
    h1 {
      font-size: 3em;
    }
    #page-container {
      padding: 2em 1em;
    }
    section section {
      position: relative;
      width: calc(100% + 2em);
      left: -1em;
    }
    #features section img {
      max-width: 100%;
    }
  }
</style>
